import type { PositionType } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { formatDeviationShort, positionTypeLabel } from "../app/format";
import { useNetworkId, useThresholds } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import { isOutOfTolerance } from "../map/deviationScope";
import { usePolledResource } from "../cockpit/hooks";
import { LineBadge } from "../cockpit/bits";

/**
 * Icône GPS du synoptique (J.8) — encodage propre à cette vue, distinct de la
 * pastille §3.1 générique : rond **vert** = position GPS, carré **rouge** =
 * position estimée, triangle **gris** = théorique / périmée.
 */
function GpsShape({ type }: { type: PositionType }) {
  const label = positionTypeLabel(type);
  let node;
  if (type === "gps") {
    node = <span aria-hidden style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--sentinelle-ok)" }} />;
  } else if (type === "estimated") {
    node = <span aria-hidden style={{ width: 9, height: 9, borderRadius: 1, background: "var(--sentinelle-signal)" }} />;
  } else {
    node = (
      <span
        aria-hidden
        style={{
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderBottom: "9px solid var(--sentinelle-ink-3)",
        }}
      />
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }} title={`Position ${label}`}>
      {node}
      <span className="is-sr-only">Position {label}</span>
    </span>
  );
}

/**
 * Synoptique complet (F3.2, CDC §5.4) — supervision globale, non géographique :
 * toutes les lignes, et pour chacune les véhicules en circulation avec leurs
 * informations essentielles. Identification non nominative uniquement.
 */
export function SynopticView() {
  const backend = useBackend();
  const networkId = useNetworkId();
  const postParam = useActivePostParam();
  const thresholds = useThresholds();

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.getSynoptic(networkId, postParam ? { post: postParam } : {}, signal),
    [backend, networkId, postParam],
    10_000,
  );

  return (
    <div>
      <h2 className="ovl" style={{ marginBottom: 12 }}>Synoptique</h2>
      {loading && !data ? (
        <p className="has-text-grey is-size-7">Chargement…</p>
      ) : error && !data ? (
        <p className="has-text-danger is-size-7">{describeError(error)}</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table className="table is-fullwidth is-narrow is-striped" style={{ fontSize: "0.78rem" }}>
          <thead>
            <tr>
              <th className="ovl" style={{ width: 60 }}>Ligne</th>
              <th className="ovl" style={{ width: 70 }}>En service</th>
              <th className="ovl">Véhicules — sortie · écart · service</th>
            </tr>
          </thead>
          <tbody>
            {(data?.lines ?? []).map((row) => {
              return (
              <tr key={row.lineId}>
                <td>
                  <LineBadge lineId={row.lineId} size={22} />
                </td>
                <td className="mono">{row.onlineCount}</td>
                <td>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {row.vehicles.map((v) => {
                      // J.8 — barre latérale rouge = hors tolérance, mêmes seuils
                      // que le panneau Avance/Retard ; fond grisé si sortie absente.
                      const oot = isOutOfTolerance(v.deviationSeconds, thresholds);
                      return (
                        <span
                          key={v.journeyId}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "2px 6px",
                            border: "1px solid var(--sentinelle-rule)",
                            borderLeft: `3px solid ${oot ? "var(--sentinelle-signal)" : "var(--sentinelle-rule)"}`,
                            background: v.absent ? "var(--sentinelle-scheme-main-ter)" : "var(--sentinelle-surface)",
                            opacity: v.absent ? 0.65 : 1,
                          }}
                        >
                          <GpsShape type={v.positionType} />
                          {/* Le n° de sortie nu (pas la forme « [ligne] [sortie] ») : la
                              ligne est déjà portée par `LineBadge` en début de rangée,
                              la répéter par véhicule était une redondance pure. */}
                          <span className="mono" style={{ fontWeight: 700 }}>
                            {v.runNumber ?? "?"}
                          </span>
                          {v.absent ? (
                            <span className="ovl" style={{ color: "var(--sentinelle-ink-3)" }}>absente</span>
                          ) : (
                            <span className="mono" style={{ color: oot ? "var(--sentinelle-signal)" : "var(--sentinelle-ink-3)" }}>
                              {formatDeviationShort(v.deviationSeconds)}
                            </span>
                          )}
                          <span className="mono has-text-grey">{v.serviceId ?? "—"}</span>
                        </span>
                      );
                    })}
                    {row.vehicles.length === 0 ? <span className="has-text-grey">aucun</span> : null}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
